/**
 * EVO — usta dasturi (loyalty) ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/loyalty/loyalty-admin.service.ts` proyeksiyalaridan
 * MAYDONMA-MAYDON ko'chirilgan (`lib/procurement.ts` bilan bir xil naqsh: bu
 * qo'lda saqlanadigan shartnoma va server ustunni qayta nomlasa TypeScript uni
 * ushlamaydi — shuning uchun har o'zgarish jonli tekshiriladi).
 *
 * ============================================================================
 * BALL — BUTUN SON, PUL — SATR. IKKALASI FAQAT BITTA JOYDA UCHRASHADI
 * ============================================================================
 * `points`, `pointsBalance`, `pointsCost` — `integer` ustunlar, ya'ni `number`
 * bo'lib keladi va shundayligicha qoladi.
 *
 * `costBase`, `stockQty`, `usd`, `pointValueUsd` — `numeric` ustunlar, ya'ni
 * SATR (`"12.0000"`, `"0.011134"`). `parseFloat` ular ustida TAQIQLANGAN
 * (`lib/format.ts` fayl boshidagi qoida).
 *
 * Ikkalasi faqat BALL QIYMATIni hosil qilishda uchrashadi:
 * `derivedPointValueUsd()` — bu faylning yagona bo'lish nuqtasi va yagona
 * yaxlitlash joyi.
 *
 * ============================================================================
 * NIMA UCHUN BU MODUL BOR — BALL BU MAJBURIYAT
 * ============================================================================
 * Usta yig'gan har bir ball — kompaniyaning QARZI (LOYALTY_SPEC §4). Uni hech
 * kim so'ramaydi, chunki u hech qanday hisobda ko'rinmaydi: pul chiqmagan,
 * tovar ketmagan, hujjat yozilmagan. Shuning uchun bu qatlamdagi har bir
 * ekran raqamning YONIDA uning pul qiymatini ham beradi — «1000 ball» hech
 * narsani anglatmaydi, «1000 ball = $11» esa anglatadi.
 *
 * Jonli tekshirilgan (2026-08-08): `moliya@evo.uz` uchun `/loyalty/liability`,
 * `/loyalty/redemptions`, `/loyalty/fraud-queue` — 200; `POST /loyalty/point-rules`
 * va `GET /loyalty/qr-batches/:id/export` — 403. `ombor@evo.uz` va
 * `sotuvchi@evo.uz` uchun `/api/loyalty/` ostidagi HAMMASI — 403.
 */
import { api } from './api';
import { compareDecimals, divDecimalByInt, mulDecimals, sumDecimals } from './decimal';
import { EMPTY_VALUE, formatNumber } from './format';
import type { Page } from './products';

// ---------------------------------------------------------------------------
// Ball qiymati — hosila raqam
// ---------------------------------------------------------------------------

/**
 * Ball qiymatining kasr xonalari soni.
 *
 * 6 — serverdagi `decDiv(totalCost, totalPoints, 6)` bilan AYNAN bir xil.
 * Kamroq xona bu raqamni yo'q qiladi: `$0.011134` ni ikki xonaga yaxlitlash
 * `$0.01` beradi va 10% xato bilan butun byudjet hisobini buzadi.
 */
export const POINT_VALUE_SCALE = 6;

/**
 * Bitta sovg'a bo'yicha ball qiymati: `costBase ÷ pointsCost`.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ BU FAYLDAGI YAGONA BO'LISH VA YAGONA YAXLITLASH NUQTASI.                 ║
 * ║                                                                          ║
 * ║ Server bu raqamni HAR SOVG'A bo'yicha qaytarmaydi — faqat katalog        ║
 * ║ o'rtachasini beradi (`liability.pointValueUsd`). Lekin sovg'a katalogi    ║
 * ║ ekranining butun ma'nosi shu taqqoslashda: agar bitta sovg'a boshqasidan  ║
 * ║ foydali bo'lsa, usta buni albatta hisoblab chiqadi va katalogning         ║
 * ║ qolgani ishlatilmaydi (LOYALTY_SPEC §7).                                 ║
 * ║                                                                          ║
 * ║ Yaxlitlash `lib/decimal.ts` → `divDecimalByInt` da, half-up, BigInt       ║
 * ║ ustida. `parseFloat` yo'q, `Number` yo'q.                                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * `null` — hisoblab bo'lmaydi (tannarx kiritilmagan yoki ball narxi nol).
 * Nol QAYTARILMAYDI: «qiymati nol» va «qiymati noma'lum» ikki xil narsa.
 */
export function derivedPointValueUsd(
  costBase: string | null | undefined,
  pointsCost: number,
): string | null {
  if (costBase === null || costBase === undefined || costBase === '') return null;
  return divDecimalByInt(costBase, pointsCost, POINT_VALUE_SCALE);
}

/**
 * Katalog bo'yicha o'rtacha ball qiymati.
 *
 * Formula SERVERDAN aynan ko'chirilgan: umumiy tannarx ÷ umumiy ball narxi
 * (nisbatlarning o'rtachasi EMAS). Ikkisi bir xil raqam bermaydi va farq
 * ekranda ko'zga tashlanadi: `liability.pointValueUsd` bilan bu yerdagi
 * qiymat bir xil bo'lishi SHART, aks holda ikki ekran ikki xil haqiqat
 * ko'rsatadi.
 *
 * Faqat FAOL va tannarxi kiritilgan sovg'alar hisobga olinadi — server ham
 * shunday (`isActive = true AND cost_base IS NOT NULL`).
 */
export function catalogueAveragePointValue(rewards: readonly Reward[]): string | null {
  const counted = rewards.filter((reward) => reward.isActive && reward.costBase !== null);
  if (counted.length === 0) return null;

  const totalCost = sumDecimals(counted.map((reward) => reward.costBase));
  const totalPoints = counted.reduce((sum, reward) => sum + reward.pointsCost, 0);
  if (totalCost === null || totalPoints <= 0) return null;

  return divDecimalByInt(totalCost, totalPoints, POINT_VALUE_SCALE);
}

/** Sovg'a qiymatining katalog o'rtachasidan chetlanishi. */
export type RewardValueFlag = 'high' | 'low' | 'ok' | 'unknown';

/**
 * Chetlanish chegarasi — o'rtachadan ±15%.
 *
 * Nima uchun FOIZ hisoblanmaydi, balki chegara KO'PAYTIRISH bilan chiqariladi:
 * ko'paytirish `lib/decimal.ts` da ANIQ (yaxlitlashsiz), bo'lish esa yana bir
 * yaxlitlash nuqtasi qo'shardi. Bizga aniq foiz kerak emas — bizga «bu sovg'a
 * qolganlaridan sezilarli farq qiladimi?» degan savolga ha/yo'q javob kerak.
 *
 * 15% — seed katalogidagi tarqalishdan ($0.0100 … $0.0120, o'rtacha $0.011134)
 * kattaroq, ya'ni izchil katalog bironta belgi bermaydi. Nomuvofiq sovg'a
 * qo'shilsa — darhol ko'rinadi.
 */
const DIVERGENCE_HIGH = '1.15';
const DIVERGENCE_LOW = '0.85';

/** Chegaraning foizdagi ko'rinishi — ekranda ogohlantirish matni uchun. */
export const DIVERGENCE_PCT = 15;

export function rewardValueFlag(
  value: string | null,
  average: string | null,
): RewardValueFlag {
  if (value === null || average === null) return 'unknown';

  const high = mulDecimals(average, DIVERGENCE_HIGH);
  const low = mulDecimals(average, DIVERGENCE_LOW);
  if (high === null || low === null) return 'unknown';

  if (compareDecimals(value, high) > 0) return 'high';
  if (compareDecimals(value, low) < 0) return 'low';
  return 'ok';
}

/**
 * Ball qiymatini ko'rsatish uchun formatlaydi: `"0.011134"` → `$0.011134`.
 *
 * `formatMoney(..., 'USD')` ISHLATILMAYDI — u 2 xonaga yaxlitlaydi va bu
 * raqamni `$0.01` ga aylantiradi. Xona soni (6) — ekran tanlovi emas, DOMEN
 * FAKTI (serverdagi `decDiv(..., 6)`), shuning uchun formatlash ham shu
 * modulda turadi va uchta ekran uni bir xil ko'rsatadi.
 */
export function formatPointValue(value: string | null | undefined): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return `$${formatNumber(value, POINT_VALUE_SCALE)}`;
}

// ---------------------------------------------------------------------------
// Vaqt tamg'asi
// ---------------------------------------------------------------------------

/**
 * Firibgarlik navbatidagi `lastAt` ni ISO ko'rinishiga keltiradi.
 *
 * `fraudQueue()` bu maydonni `max(occurred_at)::text` bilan qaytaradi, ya'ni
 * javobga XOM Postgres ko'rinishi tushadi: `2026-08-08 11:11:12.351437+00` —
 * `T` o'rniga bo'shliq va olti xonali kasr. V8 uni kechirimli o'qiydi, lekin
 * Safari bunday satrni `Invalid Date` qiladi va tergov ekranida sana o'rniga
 * `—` chiqadi. Shuning uchun normalizatsiya ma'lumot qatlamida, bir joyda.
 *
 * Boshqa endpointlarda (`occurredAt`, `requestedAt`, `lastScanAt`) sana
 * allaqachon ISO — ular bu funksiyadan o'tmaydi.
 */
export function toIsoTimestamp(raw: string | null): string | null {
  if (raw === null || raw === '') return null;
  return raw.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
}

// ---------------------------------------------------------------------------
// Majburiyat hisoboti
// ---------------------------------------------------------------------------

/** Faollashgan, lekin skanerlanmagan kodlar — mahsulot kesimida. */
export interface AwaitingScanProduct {
  readonly productId: number;
  readonly sku: string;
  readonly productName: string;
  readonly codes: number;
  readonly pointsPerCode: number;
  readonly points: number;
  /** Qoida yo'q: bu kodlar skanerlanganda ball berilmaydi — XATO belgisi. */
  readonly missingRule: boolean;
}

export interface Liability {
  /** `null` — katalogda tannarx kiritilmagan, ya'ni ball qiymati NOMA'LUM. */
  readonly pointValueUsd: string | null;
  readonly pointValueBasis: {
    readonly rewardsWithCost: number;
    readonly totalRewardCostUsd: string;
    readonly totalRewardPoints: number;
  };
  readonly outstanding: {
    readonly installers: number;
    readonly points: number;
    readonly usd: string | null;
  };
  readonly pendingRedemptions: {
    readonly count: number;
    readonly points: number;
    readonly usd: string | null;
  };
  readonly awaitingScan: {
    readonly codes: number;
    readonly points: number;
    readonly usd: string | null;
    readonly byProduct: readonly AwaitingScanProduct[];
  };
  readonly total: { readonly points: number; readonly usd: string | null };
  readonly installersTotal: number;
}

export function fetchLiability(signal?: AbortSignal): Promise<Liability> {
  return api.get<Liability>('/loyalty/liability', { ...(signal ? { signal } : {}) });
}

// ---------------------------------------------------------------------------
// Ball qoidalari
// ---------------------------------------------------------------------------

export interface PointRule {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly productName: string;
  readonly points: number;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
}

export interface PointRuleParams {
  readonly productId?: number | undefined;
  readonly activeOnly?: boolean | undefined;
}

/** Javob SAHIFALANMAYDI — server butun ro'yxatni qaytaradi (71 qoida). */
export function fetchPointRules(
  params: PointRuleParams,
  signal?: AbortSignal,
): Promise<readonly PointRule[]> {
  return api.get<readonly PointRule[]>('/loyalty/point-rules', {
    query: {
      productId: params.productId,
      ...(params.activeOnly ? { activeOnly: 'true' } : {}),
    },
    ...(signal ? { signal } : {}),
  });
}

export interface PointRuleInput {
  readonly productId: number;
  /** 0 ga RUXSAT: «ball berilmaydi» degan ONGLI qaror qoidaning yo'qligidan farq qiladi. */
  readonly points: number;
  readonly validFrom: string;
  readonly validTo?: string | null;
  readonly notes?: string | null;
}

/**
 * Yangi qoida. TAHRIRLASH YO'Q — sana bo'yicha versiyalanadi.
 *
 * Aksiya davrida ballni oshirish uchun YANGI qator qo'shiladi; o'tgan
 * skanerlar qayta hisoblanmaydi, chunki berilgan ball
 * `qr_codes.points_awarded` da muzlatilgan. Shuning uchun ekranda ham
 * «tahrirlash» tugmasi yo'q — u yolg'on va'da bo'lardi.
 */
export function createPointRule(input: PointRuleInput): Promise<PointRule & { readonly id: number }> {
  return api.post('/loyalty/point-rules', {
    productId: input.productId,
    points: input.points,
    validFrom: input.validFrom,
    ...(input.validTo ? { validTo: input.validTo } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

// ---------------------------------------------------------------------------
// Qoidasiz mahsulotlar va qoida statistikasi
// ---------------------------------------------------------------------------

/**
 * Bugun amal qiladigan qoidasi YO'Q mahsulot.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ `activatedCodes` — SHOSHILINCHLIK O'LCHOVI, oddiy ustun emas.             ║
 * ║                                                                          ║
 * ║ Faollashgan kod «tovar sotilgan» degani. Ya'ni bu mahsulotda kod bor,     ║
 * ║ usta uni skanerlashga urinishi mumkin — va qoida yo'qligi uchun skaner    ║
 * ║ `no_points_rule` bilan RAD ETILADI. Usta buni bilmaydi, u «QR            ║
 * ║ ishlamayapti» deb qo'ng'iroq qiladi.                                     ║
 * ║                                                                          ║
 * ║ Server qatorlarni AYNAN shu ustun bo'yicha KAMAYISH tartibida beradi,     ║
 * ║ shuning uchun ekran ularni QAYTA SARALAMAYDI: alifbo tartibi eng dolzarb  ║
 * ║ mahsulotni ro'yxat o'rtasiga ko'mib qo'yardi.                            ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export interface MissingRuleProduct {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly categoryId: number;
  readonly activatedCodes: number;
}

/** Standart chegara — serverdagi maksimum bilan bir xil. */
export const MISSING_RULES_LIMIT = 500;

export function fetchMissingRules(
  signal?: AbortSignal,
): Promise<readonly MissingRuleProduct[]> {
  return api.get<readonly MissingRuleProduct[]>('/loyalty/point-rules/missing', {
    query: { limit: MISSING_RULES_LIMIT },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Qoidalar bo'yicha sanoq.
 *
 * Ro'yxatdan HISOBLANMAYDI: ro'yxat filtrlangan va sahifalanmagan bo'lsa ham
 * u faqat ko'rinadigan qatorlarni biladi, `missingRules` esa umuman boshqa
 * jadvaldan (katalogdan) keladi. Ikkisini brauzerda birlashtirish ikkinchi
 * haqiqat manbaini tug'dirardi.
 */
export interface PointRuleStats {
  readonly activeRules: number;
  readonly futureRules: number;
  readonly expiredRules: number;
  readonly missingRules: number;
}

export function fetchPointRuleStats(signal?: AbortSignal): Promise<PointRuleStats> {
  return api.get<PointRuleStats>('/loyalty/point-rules/stats', {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Muvaffaqiyatli skanerlar
// ---------------------------------------------------------------------------

/**
 * Ball BERILGAN skaner.
 *
 * `scan_attempts` faqat RAD ETILGANLARNI saqlaydi — shuning uchun teskarilash
 * uchun kerak bo'lgan `qr_codes.id` ni faqat shu endpoint beradi.
 *
 * `code` bo'yicha qidiruv aniq mos kelish YOKI QISM SATR bo'yicha ishlaydi:
 * operator stiker dumidagi bir necha belgini o'qib ham qatorni topadi.
 *
 * `reversed` `point_transactions` dan hosila qilingan, `qr_codes` dan EMAS:
 * kod qatori — ball berilganining o'zgarmas DALILI va teskarilashdan keyin
 * ham o'zgarmaydi (migratsiya 0006 `trg_qr_scanned_is_final`).
 */
export interface Scan {
  readonly qrCodeId: number;
  readonly code: string;
  readonly productId: number;
  readonly sku: string;
  readonly productName: string;
  readonly batchNo: string;
  readonly pointsAwarded: number;
  readonly scannedAt: string;
  readonly scanIp: string | null;
  readonly installerId: number;
  readonly installerName: string;
  readonly installerPhone: string;
  readonly reversed: boolean;
}

export interface ScanParams {
  readonly code?: string | undefined;
  readonly installerId?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly limit?: number | undefined;
}

export function fetchScans(params: ScanParams, signal?: AbortSignal): Promise<readonly Scan[]> {
  return api.get<readonly Scan[]>('/loyalty/scans', {
    query: {
      code: params.code,
      installerId: params.installerId,
      from: params.from,
      to: params.to,
      limit: params.limit,
    },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Stiker zaxirasi
// ---------------------------------------------------------------------------

/**
 * Mahsulot bo'yicha kod zaxirasi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ STIKER TUGASHI — JIM XATO.                                               ║
 * ║                                                                          ║
 * ║ Yuk xati post qilinganda `created` kodlar faollashadi. Kod YETMASA        ║
 * ║ yetkazish TO'XTAMAYDI (va to'xtatmasligi ham kerak — stiker tugagani      ║
 * ║ uchun omborni bloklash mumkin emas), faqat jurnalga ogohlantirish         ║
 * ║ yoziladi. Ya'ni tovar stikersiz ketadi va usta skanerlaydigan narsa       ║
 * ║ topmaydi.                                                                ║
 * ║                                                                          ║
 * ║ Server qatorlarni `created` bo'yicha O'SISH tartibida beradi — zaxirasi   ║
 * ║ tugayotgan mahsulot BIRINCHI. Ekran bu tartibni saqlaydi.                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export interface StickerStock {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  /** Hali faollashmagan, ya'ni KELGUSI yetkazishlar uchun qolgan zaxira. */
  readonly created: number;
  readonly activated: number;
  readonly scanned: number;
  readonly voided: number;
}

/**
 * «Zaxira kam» chegarasi — KO'RSATISH evristikasi, biznes qoidasi EMAS.
 *
 * Serverda bunday chegara yo'q va bo'lishi ham shart emas: haqiqiy talab
 * mahsulotning sotuv tezligiga bog'liq. Ekranga esa qandaydir chegara kerak,
 * aks holda 200 qatorli jadvalda «qaysi biri xavfli?» degan savol javobsiz
 * qoladi. Nol — alohida holat va u QIZIL: bu «zaxira kam» emas, «zaxira
 * yo'q» degani.
 */
export const STICKER_LOW_THRESHOLD = 100;

export function fetchStickerStock(signal?: AbortSignal): Promise<readonly StickerStock[]> {
  return api.get<readonly StickerStock[]>('/loyalty/sticker-stock', {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// QR partiyalari
// ---------------------------------------------------------------------------

export const QR_STATUSES = ['created', 'activated', 'scanned', 'void'] as const;
export type QrStatus = (typeof QR_STATUSES)[number];

export interface QrBatch {
  readonly id: number;
  readonly batchNo: string;
  readonly productId: number;
  readonly sku: string;
  readonly productName: string;
  readonly quantity: number;
  /** Eksport qilingan sana — ikkinchi eksport shu maydondan ko'rinadi. */
  readonly printedAt: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly created: number;
  readonly activated: number;
  readonly scanned: number;
  readonly voided: number;
}

export interface QrBatchParams {
  readonly page: number;
  readonly pageSize: number;
  readonly productId?: number | undefined;
}

export function fetchQrBatches(
  params: QrBatchParams,
  signal?: AbortSignal,
): Promise<Page<QrBatch>> {
  return api.get<Page<QrBatch>>('/loyalty/qr-batches', {
    query: { page: params.page, pageSize: params.pageSize, productId: params.productId },
    ...(signal ? { signal } : {}),
  });
}

/** `MAX_BATCH_QUANTITY` — serverdagi chegara bilan AYNAN bir xil. */
export const MAX_BATCH_QUANTITY = 20_000;

export interface QrBatchInput {
  readonly productId: number;
  readonly quantity: number;
  readonly notes?: string | null;
}

export interface QrBatchCreated {
  readonly id: number;
  readonly batchNo: string;
  readonly productId: number;
  readonly sku: string;
  readonly quantity: number;
  readonly status: string;
}

export function createQrBatch(input: QrBatchInput): Promise<QrBatchCreated> {
  return api.post<QrBatchCreated>('/loyalty/qr-batches', {
    productId: input.productId,
    quantity: input.quantity,
    ...(input.notes ? { notes: input.notes } : {}),
  });
}

export interface QrExportCode {
  readonly serial: number;
  readonly code: string;
  readonly status: string;
}

export interface QrExport {
  readonly batch: {
    readonly id: number;
    readonly batchNo: string;
    readonly productId: number;
    readonly sku: string;
    readonly productName: string;
    readonly quantity: number;
  };
  readonly codes: readonly QrExportCode[];
  /**
   * Qaysi rejimda olingani. `print` — kodlar ochiq matnda, partiya uchun
   * BIR MARTA; `audit` — `code` ustunisiz, cheksiz marta.
   */
  readonly mode: 'print' | 'audit';
  /** `print` rejimi partiyani egallaydi. Ikkinchi urinish 409 beradi. */
  readonly printedAt: string | null;
}

/**
 * Partiyani ochiq matnda oladi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ BU O'QISH EMAS — KOD CHIQARISH BILAN TENG AMAL.                          ║
 * ║                                                                          ║
 * ║ Ruxsat: `settings.edit` VA `export.excel` — IKKALASI. Kod YARATISH      ║
 * ║ (`loyalty.qr.generate`) va kodlarni O'QIB OLISH har xil huquq.          ║
 * ║ Javob — butun rulodagi kodlar, va ularni olgan odam tovar sotilishi      ║
 * ║ bilan hamma ballni yig'ib oladi.                                         ║
 * ║                                                                          ║
 * ║ CHAQIRUVNING O'ZI auditga `export` sifatida tushadi va partiyaning       ║
 * ║ `printed_at` ini belgilaydi — ya'ni faylni saqlamasdan bekor qilib       ║
 * ║ bo'lmaydi. Shuning uchun ekranda bir bosishlik tugma YO'Q: avval         ║
 * ║ tasdiqlash qadami, unda aynan shu fakt yozilgan.                         ║
 * ║                                                                          ║
 * ║ `format=json` so'raladi, `csv` emas: `apiFetch` javobni `JSON.parse`     ║
 * ║ qiladi va CSV bilan yiqilardi. Fayl brauzerda `lib/export-csv.ts`        ║
 * ║ orqali AYNAN shu javobdan quriladi — bu «eksport ikkinchi so'rov         ║
 * ║ qilmaydi» qoidasi bilan ham mos.                                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function exportQrBatch(batchId: number): Promise<QrExport> {
  /*
   * `POST`, `GET` EMAS — ataylab.
   *
   * Ikki sabab: (1) amal yon ta'sirli — partiyaning `printed_at` ini
   * belgilaydi va auditga `export` sifatida tushadi, ya'ni u RESURS OLISH
   * emas; (2) `GET` bo'lganda kodlar URL da qolardi — brauzer tarixi,
   * proxy log'i va `Referer` sarlavhasi orqali tarqalardi.
   *
   * `mode: 'print'` — bosmaxona uchun ochiq kodlar, faqat `created`
   * holatidagilar. `activated` kodlar HECH QACHON chiqmaydi: ular ayni
   * paytda ballga aylantirilishi mumkin.
   */
  return api.post<QrExport>(`/loyalty/qr-batches/${batchId}/export`, {
    format: 'json',
    mode: 'print',
  });
}

// ---------------------------------------------------------------------------
// Sovg'a katalogi
// ---------------------------------------------------------------------------

export interface Reward {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly pointsCost: number;
  /** Kompaniyaga tannarxi (USD, SATR). `null` — ball qiymatini o'lchab bo'lmaydi. */
  readonly costBase: string | null;
  /** `null` — cheksiz (sertifikat kabi). `"0.0000"` — tugagan. */
  readonly stockQty: string | null;
  readonly imageUrl: string | null;
  readonly sortOrder: number;
  readonly isActive: boolean;
}

export function fetchRewards(signal?: AbortSignal): Promise<readonly Reward[]> {
  return api.get<readonly Reward[]>('/loyalty/rewards', { ...(signal ? { signal } : {}) });
}

export interface RewardCreateInput {
  readonly code: string;
  readonly name: string;
  readonly description?: string | null;
  readonly pointsCost: number;
  readonly costBase?: string | null;
  readonly stockQty?: string | null;
  readonly sortOrder?: number;
}

export function createReward(input: RewardCreateInput): Promise<Reward> {
  return api.post<Reward>('/loyalty/rewards', {
    code: input.code,
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    pointsCost: input.pointsCost,
    ...(input.costBase ? { costBase: input.costBase } : {}),
    ...(input.stockQty ? { stockQty: input.stockQty } : {}),
    sortOrder: input.sortOrder ?? 0,
  });
}

/** Tahrirlashda `code` YO'Q: kod — barqaror identifikator, uni o'zgartirish tarixni uzadi. */
export interface RewardUpdateInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly pointsCost?: number;
  readonly costBase?: string | null;
  readonly stockQty?: string | null;
  readonly sortOrder?: number;
  readonly isActive?: boolean;
}

export function updateReward(id: number, input: RewardUpdateInput): Promise<Reward> {
  return api.put<Reward>(`/loyalty/rewards/${id}`, input);
}

// ---------------------------------------------------------------------------
// Almashtirishlar
// ---------------------------------------------------------------------------

export const REDEMPTION_STATUSES = [
  'requested',
  'approved',
  'issued',
  'rejected',
  'cancelled',
] as const;
export type RedemptionStatus = (typeof REDEMPTION_STATUSES)[number];

export interface Redemption {
  readonly id: number;
  readonly docNo: string;
  readonly installerId: number;
  readonly installerName: string;
  readonly installerPhone: string;
  readonly rewardId: number;
  readonly rewardName: string;
  /** So'rov paytidagi narx — katalog keyin o'zgarsa bu o'zgarmaydi. */
  readonly pointsCost: number;
  readonly status: string;
  readonly requestedAt: string;
  readonly approvedAt: string | null;
  readonly issuedAt: string | null;
  readonly rejectReason: string | null;
  readonly deliveryNote: string | null;
}

export interface RedemptionParams {
  readonly page: number;
  readonly pageSize: number;
  readonly status?: RedemptionStatus | undefined;
  readonly installerId?: number | undefined;
}

export function fetchRedemptions(
  params: RedemptionParams,
  signal?: AbortSignal,
): Promise<Page<Redemption>> {
  return api.get<Page<Redemption>>('/loyalty/redemptions', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      status: params.status,
      installerId: params.installerId,
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * `requested` → `approved`. Pul harakati yo'q: ball SO'ROV PAYTIDA yechilgan.
 */
export function approveRedemption(id: number): Promise<Redemption> {
  return api.post<Redemption>(`/loyalty/redemptions/${id}/approve`);
}

/**
 * Rad etish — ball TESKARI YOZUV bilan qaytariladi.
 *
 * Javobda `pointsRestored` va `balanceAfter` bor: operator rad etish
 * «bepul» emasligini KO'RISHI kerak — ball ustaga qaytadi va bu balansda
 * darhol ko'rinadi.
 */
export interface RedemptionRejectResult extends Redemption {
  readonly pointsRestored: number;
  readonly balanceAfter: number;
}

export function rejectRedemption(id: number, reason: string): Promise<RedemptionRejectResult> {
  return api.post<RedemptionRejectResult>(`/loyalty/redemptions/${id}/reject`, { reason });
}

/**
 * `approved` → `issued`. `requested` dan to'g'ridan-to'g'ri O'TILMAYDI:
 * tasdiqlovchi va topshiruvchi bir odam bo'lmasligi kerak (vazifalar ajratilishi).
 */
export function issueRedemption(id: number, notes?: string | null): Promise<Redemption> {
  return api.post<Redemption>(`/loyalty/redemptions/${id}/issue`, notes ? { notes } : {});
}

/** Holat o'tishlari — tugmalar shu funksiyalarga qarab ko'rinadi. */
export function canApproveRedemption(status: string): boolean {
  return status === 'requested';
}

export function canIssueRedemption(status: string): boolean {
  return status === 'approved';
}

export function canRejectRedemption(status: string): boolean {
  return status === 'requested' || status === 'approved';
}

// ---------------------------------------------------------------------------
// Ustalar
// ---------------------------------------------------------------------------

export interface Installer {
  readonly id: number;
  /** Faqat raqamlar: `998901234567`. Unikal — bitta odam ko'p hisob ochmasligi uchun. */
  readonly phone: string;
  readonly fullName: string;
  readonly region: string | null;
  readonly city: string | null;
  readonly pointsBalance: number;
  readonly pointsEarnedTotal: number;
  readonly pointsRedeemedTotal: number;
  readonly isBlocked: boolean;
  readonly blockReason: string | null;
  readonly verifiedAt: string | null;
  readonly lastScanAt: string | null;
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
}

export interface InstallerParams {
  readonly page: number;
  readonly pageSize: number;
  readonly q?: string | undefined;
  readonly blocked?: boolean | undefined;
}

export function fetchInstallers(
  params: InstallerParams,
  signal?: AbortSignal,
): Promise<Page<Installer>> {
  return api.get<Page<Installer>>('/loyalty/installers', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      q: params.q,
      ...(params.blocked === undefined ? {} : { blocked: params.blocked ? 'true' : 'false' }),
    },
    ...(signal ? { signal } : {}),
  });
}

// --- Kartochka: daftar va rekonsiliatsiya -----------------------------------

export interface InstallerDetailRow extends Installer {
  readonly isActive: boolean;
  /** Telegram ulangan — ball qo'shilgani haqida xabar boradi. */
  readonly telegramLinked: boolean;
  readonly scanCount: number;
  /** Rad etilgan urinishlar — firibgarlik navbatidagi qatorning shu ustadagi ulushi. */
  readonly rejectedCount: number;
}

/**
 * Ball daftarining bitta yozuvi.
 *
 * `sourceLabel` serverda hal qilinadi: skaner uchun SKU, almashtirish uchun
 * sovg'a nomi. Ya'ni daftar «bu 1500 ball qayerdan keldi?» degan savolga
 * qo'shimcha so'rovsiz javob beradi.
 */
export interface LedgerEntry {
  readonly id: number;
  readonly direction: string;
  /** Ishorali: + yig'ildi, − sarflandi. */
  readonly points: number;
  readonly balanceAfter: number;
  readonly sourceTable: string;
  readonly sourceId: number | null;
  readonly notes: string | null;
  readonly occurredAt: string;
  readonly isReversal: boolean;
  readonly reversalOfId: number | null;
  readonly reversedById: number | null;
  readonly sourceLabel: string | null;
}

/**
 * Denormalizatsiyalangan balans va daftar yig'indisining solishtiruvi.
 *
 * `ok === false` — BU BUG, ko'rsatish tafsiloti emas. `installers.points_balance`
 * tezkor o'qish uchun saqlanadi, haqiqat esa `point_transactions` da
 * (`stock_balances` bilan aynan bir xil naqsh). Ikkisi ajralib ketgan bo'lsa,
 * ustaga ko'rsatilgan balans ham, majburiyat hisoboti ham ishonchsiz.
 */
export interface Reconciliation {
  readonly balance: number;
  readonly ledgerTotal: number;
  readonly ok: boolean;
  readonly message: string;
}

export interface InstallerDetail {
  readonly installer: InstallerDetailRow;
  /** Oxirgi 200 yozuv — yangisi birinchi. */
  readonly ledger: readonly LedgerEntry[];
  readonly reconciliation: Reconciliation;
}

export function fetchInstaller(id: number, signal?: AbortSignal): Promise<InstallerDetail> {
  return api.get<InstallerDetail>(`/loyalty/installers/${id}`, { ...(signal ? { signal } : {}) });
}

/** Kartochkadagi almashtirishlar — ro'yxat javobidan qisqaroq proyeksiya. */
export interface InstallerRedemption {
  readonly id: number;
  readonly docNo: string;
  readonly rewardName: string;
  readonly pointsCost: number;
  readonly status: string;
  readonly requestedAt: string;
  readonly approvedAt: string | null;
  readonly issuedAt: string | null;
  readonly rejectReason: string | null;
}

export function fetchInstallerRedemptions(
  id: number,
  signal?: AbortSignal,
): Promise<readonly InstallerRedemption[]> {
  return api.get<readonly InstallerRedemption[]>(`/loyalty/installers/${id}/redemptions`, {
    ...(signal ? { signal } : {}),
  });
}

export interface InstallerBlockResult {
  readonly id: number;
  readonly fullName: string;
  readonly isBlocked: boolean;
  readonly blockReason: string | null;
  /** Javobda ATAYLAB bor: blokdan keyin ham ball JOYIDA qoladi. */
  readonly pointsBalance: number;
}

/**
 * Bloklash — skanerlash to'xtaydi, BALL QOLADI.
 *
 * Ballni ham o'chirish vasvasasi kuchli, lekin noto'g'ri: blok SHUBHA
 * asosida qo'yiladi va tekshiruv uni rad etishi mumkin. Yig'ilgan ball
 * kompaniyaning majburiyati va uni tekshiruvdan oldin o'chirish halol ustani
 * jazolash demak. Ballni qaytarib olish — ALOHIDA amal (`reverseScan`).
 */
export function setInstallerBlocked(
  id: number,
  isBlocked: boolean,
  reason?: string | null,
): Promise<InstallerBlockResult> {
  return api.put<InstallerBlockResult>(`/loyalty/installers/${id}/block`, {
    isBlocked,
    ...(isBlocked && reason ? { reason } : {}),
  });
}

// ---------------------------------------------------------------------------
// Firibgarlik navbati
// ---------------------------------------------------------------------------

export const SCAN_REJECT_REASONS = [
  'not_found',
  'already_scanned',
  'not_activated',
  'void',
  'installer_blocked',
  'rate_limited',
  'no_points_rule',
] as const;
export type ScanRejectReason = (typeof SCAN_REJECT_REASONS)[number];

export interface FraudByInstaller {
  readonly installerId: number | null;
  readonly fullName: string;
  readonly phone: string;
  readonly isBlocked: boolean;
  readonly attempts: number;
  readonly notFound: number;
  readonly alreadyScanned: number;
  readonly notActivated: number;
  readonly rateLimited: number;
  readonly distinctCodes: number;
  /** XOM Postgres vaqti — `toIsoTimestamp()` dan o'tkaziladi. */
  readonly lastAt: string;
}

export interface FraudByCode {
  readonly code: string;
  readonly attempts: number;
  readonly distinctInstallers: number;
  /** Vergul bilan ajratilgan sabablar (`string_agg`). */
  readonly reasons: string | null;
  readonly lastAt: string;
}

export interface FraudByIp {
  readonly ipAddress: string | null;
  readonly attempts: number;
  readonly distinctInstallers: number;
  readonly lastAt: string;
}

export interface FraudAttempt {
  readonly id: number;
  readonly installerId: number | null;
  readonly fullName: string | null;
  readonly code: string;
  readonly reason: string;
  readonly ipAddress: string | null;
  readonly occurredAt: string;
}

export interface FraudSummary {
  readonly total: number;
  readonly notFound: number;
  readonly alreadyScanned: number;
  readonly notActivated: number;
  readonly blocked: number;
  readonly rateLimited: number;
  readonly noRule: number;
}

export interface FraudQueue {
  readonly windowDays: number;
  readonly minAttempts: number;
  readonly summary: FraudSummary | null;
  readonly byInstaller: readonly FraudByInstaller[];
  readonly byCode: readonly FraudByCode[];
  readonly byIp: readonly FraudByIp[];
  readonly recent: readonly FraudAttempt[];
}

export interface FraudParams {
  readonly days: number;
  readonly minAttempts: number;
  readonly limit: number;
}

/** Serverdagi default'lar bilan AYNAN bir xil (`FraudQuerySchema`). */
export const FRAUD_DEFAULTS: FraudParams = { days: 7, minAttempts: 5, limit: 50 };

export function fetchFraudQueue(
  params: FraudParams,
  signal?: AbortSignal,
): Promise<FraudQueue> {
  return api.get<FraudQueue>('/loyalty/fraud-queue', {
    query: { days: params.days, minAttempts: params.minAttempts, limit: params.limit },
    ...(signal ? { signal } : {}),
  });
}

export interface ScanReverseResult {
  readonly qrCodeId: number;
  readonly code: string;
  readonly installerId: number;
  readonly pointsReversed: number;
  readonly balanceAfter: number;
  readonly reversalTransactionId: number;
  /** Kod holati ATAYLAB o'zgarmaydi — u ball berilganining DALILI. */
  readonly qrStatus: string;
}

/**
 * Berilgan ballni teskari yozuv bilan qaytarib oladi.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ IDENTIFIKATOR — `qr_codes.id`, KODNING O'ZI EMAS.                        │
 * │                                                                          │
 * │ Uni operator QO'LDA KIRITMAYDI: `fetchScans({ code })` kod bo'yicha      │
 * │ (qism satr ham bo'ladi) qatorni topadi va ekran shu qatordan `qrCodeId`  │
 * │ ni oladi. Ya'ni oqim «kodni qidir → qatorni tanla → tasdiqla».           │
 * │                                                                          │
 * │ Ikki marta teskarilash serverda 409 beradi, lekin ekran bu holatga       │
 * │ yetib bormasligi kerak: `Scan.reversed` allaqachon teskarilangan         │
 * │ qatorni ko'rsatadi va tugma o'chirilgan bo'ladi.                         │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * 422 `REVERSAL_EXCEEDS_BALANCE` — usta ballni allaqachon sarflagan. Bu
 * texnik xato emas, MA'MURIY qaror talab qiladigan holat va ekran uni
 * shundayligicha ko'rsatadi.
 */
export function reverseScan(qrCodeId: number, reason: string): Promise<ScanReverseResult> {
  return api.post<ScanReverseResult>(`/loyalty/scans/${qrCodeId}/reverse`, { reason });
}

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

export const loyaltyKeys = {
  all: ['loyalty'] as const,
  liability: ['loyalty', 'liability'] as const,
  pointRules: (params: PointRuleParams) => ['loyalty', 'point-rules', params] as const,
  qrBatches: (params: QrBatchParams) => ['loyalty', 'qr-batches', params] as const,
  rewards: ['loyalty', 'rewards'] as const,
  redemptions: (params: RedemptionParams) => ['loyalty', 'redemptions', params] as const,
  installers: (params: InstallerParams) => ['loyalty', 'installers', params] as const,
  fraudQueue: (params: FraudParams) => ['loyalty', 'fraud-queue', params] as const,
  missingRules: ['loyalty', 'point-rules', 'missing'] as const,
  pointRuleStats: ['loyalty', 'point-rules', 'stats'] as const,
  scans: (params: ScanParams) => ['loyalty', 'scans', params] as const,
  stickerStock: ['loyalty', 'sticker-stock'] as const,
  installer: (id: number) => ['loyalty', 'installer', id] as const,
  installerRedemptions: (id: number) => ['loyalty', 'installer-redemptions', id] as const,
};
